 import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Skeleton,
  Alert,
  useTheme,
  useMediaQuery,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import BarChartIcon from '@mui/icons-material/BarChart';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const COLORS = ['#1976d2', '#00c853', '#ff9800', '#f44336', '#9c27b0', '#00bcd4'];

// Clean, minimal styled components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  border: '1px solid rgba(0,0,0,0.06)',
  transition: 'box-shadow 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
  }
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-around',
  marginTop: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: '#fafafa',
  borderRadius: 12,
}));

const StatItem = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  minWidth: 80,
}));

const RoleItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  margin: theme.spacing(0.5, 0),
  backgroundColor: '#f8f9fa',
  borderRadius: 8,
  border: '1px solid #e9ecef',
}));

const ColorDot = styled(Box)(({ color }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: color,
  marginRight: 8,
  flexShrink: 0,
}));

const UserActivity = () => {
  const [userCounts, setUserCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const fetchUserCounts = async () => {
      try {
        const response = await axios.get('/api/v1/get-admin-user-count', {
          withCredentials: true,
        });

        if (!response.data || !Array.isArray(response.data.roleCounts)) {
          throw new Error('Invalid data format from API');
        }

        const counts = response.data.roleCounts.map(item => ({
          name: item.role,
          value: parseInt(item.count),
        }));

        setUserCounts(counts);
        setLoading(false);
      } catch (err) {
        console.error('API Error:', err);
        setError('Failed to load user data');
        setLoading(false);
      }
    };

    fetchUserCounts();
  }, []);

  const total = userCounts.reduce((sum, u) => sum + u.value, 0);
  const maxRole = userCounts.length > 0 ? Math.max(...userCounts.map(u => u.value)) : 0;

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percent = ((data.value / total) * 100).toFixed(1);
      return (
        <Box
          sx={{
            bgcolor: 'white',
            p: 1.5,
            borderRadius: 1,
            boxShadow: 2,
            border: '1px solid #e0e0e0',
            minWidth: 120,
          }}
        >
          <Typography variant="body2" fontWeight="600">
            {data.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {data.value} users ({percent}%)
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <HeaderContainer>
            <Skeleton variant="circular" width={48} height={48} />
            <Box>
              <Skeleton variant="text" width={200} height={28} />
              <Skeleton variant="text" width={150} height={20} />
            </Box>
          </HeaderContainer>
          <Skeleton variant="rectangular" width="100%" height={280} sx={{ borderRadius: 2 }} />
          <Box mt={2}>
            <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 2 }} />
          </Box>
        </CardContent>
      </StyledCard>
    );
  }

  if (error) {
    return (
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        </CardContent>
      </StyledCard>
    );
  }

  if (userCounts.length === 0) {
    return (
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            No user data available
          </Alert>
        </CardContent>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <HeaderContainer>
          <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
            <BarChartIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="600" color="#544040">
              Admin User Count
            </Typography>
            <Typography variant="body2" color="text.secondary">
              User role distribution
            </Typography>
          </Box>
        </HeaderContainer>

        {/* Chart */}
        <Box sx={{ width: '100%', height: 280, mb: 2 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={userCounts}
                cx="50%"
                cy="50%"
                outerRadius={isMobile ? 85 : 100}
                fill="#8884d8"
                dataKey="value"
                stroke="white"
                strokeWidth={2}
              >
                {userCounts.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              {!isMobile && (
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  iconType="circle"
                  wrapperStyle={{ fontSize: '14px' }}
                />
              )}
            </PieChart>
          </ResponsiveContainer>
        </Box>

        {/* Mobile Legend */}
        {isMobile && (
          <Box sx={{ mb: 2 }}>
            {userCounts.map(({ name, value }, index) => {
              const percent = ((value / total) * 100).toFixed(1);
              return (
                <RoleItem key={name}>
                  <Box display="flex" alignItems="center">
                    <ColorDot color={COLORS[index % COLORS.length]} />
                    <Typography variant="body2" fontWeight="500">
                      {name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {value} ({percent}%)
                  </Typography>
                </RoleItem>
              );
            })}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Stats Summary */}
        <StatsContainer>
          <StatItem>
            <Typography variant="h5" fontWeight="700" color="primary.main">
              {total}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total Users
            </Typography>
          </StatItem>
          <StatItem>
            <Typography variant="h5" fontWeight="700" color="secondary.main">
              {userCounts.length}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              User Roles
            </Typography>
          </StatItem>
          <StatItem>
            <Typography variant="h5" fontWeight="700" color="success.main">
              {maxRole}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Largest Role
            </Typography>
          </StatItem>
        </StatsContainer>
      </CardContent>
    </StyledCard>
  );
};

export default UserActivity;